/**
 * Initial Setup Modal Component
 * 
 * Displays the initial configuration screen for new users.
 * Blocks navigation until the user completes the required setup:
 * - Ensures the default agent exists using the registered user name
 * - Sets the Budget Period (PO) based on income payment day
 */
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { agentService, budgetPeriodService } from "@/services/financial.service";
import { toast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import type { BudgetPeriod } from "@/types/financial";

const FIRST_ACCESS_DEFAULT_AGENT_DESCRIPTION = "Agente padrão definido no primeiro acesso";

interface InitialSetupModalProps {
  open: boolean;
  defaultPaymentDay?: number;
  defaultAgentName?: string;
  onComplete: () => void;
  onSuccess?: () => void;
}

export const InitialSetupModal = ({
  open,
  defaultPaymentDay = 5,
  defaultAgentName,
  onComplete,
  onSuccess,
}: InitialSetupModalProps) => {
  const queryClient = useQueryClient();
  const normalizedAgentName = (defaultAgentName || "").trim();
  const [agentName, setAgentName] = useState(normalizedAgentName || "Agente Padrão");
  const [paymentDay, setPaymentDay] = useState<number>(defaultPaymentDay);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setAgentName(normalizedAgentName || "Agente Padrão");
  }, [normalizedAgentName]);

  const handleSubmit = async () => {
    if (paymentDay < 1 || paymentDay > 31) {
      toast({ title: "Dia do mês deve estar entre 1 e 31", variant: "destructive" });
      return;
    }

    try {
      setIsSubmitting(true);

      // Garante existência do agente padrão sem forçar flags de pagador/recebedor.
      const resolvedAgentName = agentName.trim() || "Agente Padrão";
      const existingAgents = await agentService.getAll(true);
      const currentDefaultAgent = existingAgents.find((agent) => agent.is_default_agent);
      const shouldApplyFirstAccessDescription = (description?: string | null) => {
        const normalizedDescription = (description || "").trim();
        return (
          normalizedDescription.length === 0 ||
          normalizedDescription === FIRST_ACCESS_DEFAULT_AGENT_DESCRIPTION
        );
      };

      if (currentDefaultAgent) {
        const shouldUpdateFirstAccessDescription = shouldApplyFirstAccessDescription(
          currentDefaultAgent.description
        );
        const needsFirstAccessDescriptionUpdate =
          shouldUpdateFirstAccessDescription &&
          (currentDefaultAgent.description || "").trim() !==
            FIRST_ACCESS_DEFAULT_AGENT_DESCRIPTION;
        const needsUpdate =
          currentDefaultAgent.name !== resolvedAgentName ||
          !currentDefaultAgent.is_default_agent ||
          !currentDefaultAgent.is_active ||
          needsFirstAccessDescriptionUpdate;

        if (needsUpdate) {
          await agentService.update(currentDefaultAgent.id, {
            name: resolvedAgentName,
            is_default_agent: true,
            is_active: true,
            ...(shouldUpdateFirstAccessDescription
              ? { description: FIRST_ACCESS_DEFAULT_AGENT_DESCRIPTION }
              : {}),
          });
        }
      } else {
        await agentService.create({
          name: resolvedAgentName,
          agent_type: "person" as const,
          is_default_agent: true,
          description: FIRST_ACCESS_DEFAULT_AGENT_DESCRIPTION,
        });
      }

      // Define ou atualiza o período orçamentário inicial
      let currentPeriod: BudgetPeriod | null = null;
      try {
        currentPeriod = await budgetPeriodService.getCurrent();
      } catch {
        currentPeriod = null;
      }

      if (!currentPeriod) {
        currentPeriod = await budgetPeriodService.create({
          payment_day: paymentDay,
        });
      } else if (currentPeriod.payment_day !== paymentDay) {
        currentPeriod = await budgetPeriodService.update(currentPeriod.id, {
          payment_day: paymentDay,
        });
      }

      // Navega para o período atual para garantir que o is_current está definido corretamente
      const currentNavigatedPeriod = await budgetPeriodService.navigate("current");

      // Atualiza cache local imediatamente e força sincronização completa.
      queryClient.setQueryData(["financial", "budgetPeriods", "current"], currentNavigatedPeriod);
      await queryClient.invalidateQueries({ queryKey: ["financial"] });
      await queryClient.refetchQueries({ queryKey: ["financial", "budgetPeriods", "current"] });
      await queryClient.refetchQueries({ queryKey: ["financial", "budgetPeriods", "navigation"] });
      await queryClient.refetchQueries({ queryKey: ["financial", "settings"] });
      await queryClient.refetchQueries({ queryKey: ["financial", "agents"] });

      toast({
        title: "Configuração inicial concluída",
        description: "Agente padrão e período orçamentário configurados.",
      });

      // Chama onSuccess para notificar que a configuração foi concluída com sucesso
      onSuccess?.();
      onComplete();
    } catch (error: unknown) {
      let errorMessage = "Tente novamente em instantes.";
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === "object" && error !== null) {
        const errorObj = error as { response?: { data?: { detail?: string } } };
        errorMessage = errorObj.response?.data?.detail ?? errorMessage;
      }
      toast({
        title: "Não foi possível concluir a configuração inicial",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      {/* Sem X: a configuração inicial só termina por "Salvar e continuar"
          (chamado 7116). O X não fechava nada e confundia. */}
      <DialogContent className="sm:max-w-lg" hideCloseButton>
        <DialogHeader className="text-center space-y-2">
          <DialogTitle className="text-xl font-semibold uppercase tracking-wide text-center">
            CONFIGURAÇÕES INICIAIS
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground text-center">
            Conheça o agente padrão da sua conta e defina o período orçamentário antes de planejar seu orçamento.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Seção 1 - Agente Padrão */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">1 - Agente Padrão</Label>
            <Input
              id="agent-name"
              value={agentName}
              onChange={(event) => setAgentName(event.target.value)}
              placeholder="Nome do agente padrão"
              className="w-full"
            />
            <p className="text-xs text-muted-foreground">
              Este é o seu Agente Padrão na conta. Você poderá incluir, excluir e editar agentes no Menu &quot;Conta&quot; - &quot;Agentes&quot;.
            </p>
          </div>

          {/* Seção 2 - Período Orçamentário */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">2 - Período Orçamentário</Label>
            <p className="text-xs text-muted-foreground">
              É o intervalo de 30 dias entre o recebimento de uma receita e outra. Informe a data de recebimento de sua receita principal. Esta definirá o seu Período Orçamentário. Para alterar esta data, acesse o &quot;Dashboard Financeiro&quot; e clique em &quot;Configurar Período&quot;.
            </p>
            
            <div className="space-y-2">
              <Label htmlFor="payment-day" className="text-sm font-medium flex items-center gap-2">
                Data de início do Período Orçamentário
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      <p>Selecione o dia do mês que marcará o início do seu período orçamentário.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
              <Select
                value={String(paymentDay)}
                onValueChange={(value) => setPaymentDay(Number(value))}
              >
                <SelectTrigger id="payment-day" className="w-full">
                  <SelectValue placeholder="Selecione o dia" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <SelectItem key={day} value={String(day)}>
                      Dia {day}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button onClick={handleSubmit} disabled={isSubmitting} className="w-full sm:w-auto uppercase font-medium">
            {isSubmitting ? "Salvando..." : "Salvar e Continuar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
